import React, { useState, useReducer } from 'react'
import PropTypes from 'prop-types'
import { DndContext } from '@dnd-kit/core'
import { shuffleArray } from '@helpers/utilities'
import { DroppableCategory } from '@components/DroppableCategory'
import { DraggableImage } from '@components/DraggableImage'
import { cloneDeep } from 'lodash'
import ExerciseBlock from '@components/ExerciseBlock'
import ExerciseHeader from '@components/ExerciseHeader'
import Heading from '@components/Heading'
import {
  BUTTON_TYPE,
  DIVIDER_SIZE,
  HEADING_TYPE,
  TEXT_SIZES,
} from '@helpers/constants'
import Divider from '@components/Divider'
import Button from '@components/Button'
import { useResponsive } from '@hooks/useResponsive'
import Text from '@components/Text'

const IMAGE_LIST = 'image-list'
const DROPPABLE_ID_PREFIX = 'droppable-'
const DRAGGABLE_ID_PREFIX = 'draggable-'
const getDroppableIndex = (droppableId) =>
  droppableId.replace(DROPPABLE_ID_PREFIX, '')

const reducer = (state, { type, payload }) => {
  let imageIndex = null
  const { images, categories } = cloneDeep(state)
  const { fromIndex, toIndex, draggableId } = payload

  switch (type) {
    case 'moveFromImagesToCategory':
      imageIndex = images.findIndex((image) => image.id === draggableId)

      categories[toIndex].placedImages.push(images[imageIndex])
      images.splice(imageIndex, 1)

      return {
        images: images,
        categories: categories,
      }
    case 'moveFromCategoryToCategory':
      if (fromIndex === toIndex) return state

      imageIndex = categories[fromIndex].placedImages.findIndex(
        (image) => image.id === draggableId
      )

      categories[toIndex].placedImages.push(
        categories[fromIndex].placedImages[imageIndex]
      )
      categories[fromIndex].placedImages.splice(imageIndex, 1)

      return {
        ...state,
        categories: categories,
      }
    case 'moveFromCategoryToImages':
      imageIndex = categories[fromIndex].placedImages.findIndex(
        (image) => image.id === draggableId
      )

      if (imageIndex !== -1) {
        images.push(categories[fromIndex].placedImages[imageIndex])
        categories[fromIndex].placedImages.splice(imageIndex, 1)
      }

      return {
        images: images,
        categories: categories,
      }
    case 'reset':
      return getInitialState(payload.categories)
    default:
      throw new Error('unknown action')
  }
}

const getInitialState = (cats) => {
  const categories = cloneDeep(cats)

  return {
    images: shuffleArray(
      categories
        .flatMap(({ images }, index) =>
          images.map((image) => ({ ...image, parentCategoryIndex: index }))
        )
        .filter((image) => image.filename || image.previewUrl)
        .map((image, index) => ({ ...image, id: DRAGGABLE_ID_PREFIX + index }))
    ),
    categories: categories.map((category) => ({
      ...category,
      placedImages: [],
    })),
  }
}

const DragImageToCategory = (props) => {
  if (!props.categories) return null

  const {
    windowSize: { width },
  } = useResponsive()

  const { name, preamble, image, showPanel, onShowPanel, onClosePanel } = props
  const [{ images, categories }, dispatch] = useReducer(
    reducer,
    props.categories,
    getInitialState
  )
  const [draggingId, setDraggingId] = useState(false)
  const allAnswersCorrect =
    images.length === 0 &&
    categories.every((category, index) =>
      category.placedImages.every(
        (image) => image.parentCategoryIndex === index
      )
    )
  const totalImages = categories.reduce(
    (acc, { images }) =>
      acc +
      images.filter((image) => image?.filename || image?.previewUrl).length,
    0
  )

  const reset = () => {
    dispatch({
      type: 'reset',
      payload: {
        categories: props.categories,
      },
    })
  }

  const onDragEnd = (data) => {
    const { active, over } = data
    const fromId = active.data.current.fromDroppableId
    setDraggingId(false)

    if (over && (fromId !== IMAGE_LIST || over.id !== IMAGE_LIST)) {
      dispatch({
        type:
          fromId === IMAGE_LIST && over.id !== IMAGE_LIST
            ? 'moveFromImagesToCategory'
            : 'moveFromCategoryToCategory',
        payload: {
          fromIndex:
            fromId !== IMAGE_LIST ? getDroppableIndex(fromId) : undefined,
          toIndex: getDroppableIndex(over.id),
          draggableId: active.id,
        },
      })
    }
  }

  const resetImage = (draggableId, fromIndex) => {
    dispatch({
      type: 'moveFromCategoryToImages',
      payload: {
        fromIndex,
        draggableId,
      },
    })
  }

  return (
    <div>
      <ExerciseBlock
        name={name}
        onShowPanel={onShowPanel}
        preamble={preamble}
        image={image}
      >
        Starta övning
      </ExerciseBlock>

      <ExerciseHeader
        exerciseName={name}
        closePanel={onClosePanel}
        openPanel={showPanel}
        width={width < 1024 ? '50%' : null}
      >
        <DndContext
          autoScroll={false}
          onDragStart={(data) => setDraggingId(data.active.id)}
          onDragEnd={(data) => {
            onDragEnd(data)
          }}
        >
          <div className={'flex flex-col h-full'}>
            {allAnswersCorrect ? (
              <div
                style={{ flexBasis: '280px' }}
                className={'flex-none flex flex-col items-center'}
              >
                <Divider size={DIVIDER_SIZE.SMALL} />
                <Heading
                  size={TEXT_SIZES.XXLARGE}
                  headingLevel={HEADING_TYPE.H1}
                >
                  Bra jobbat!
                </Heading>
                <Divider size={DIVIDER_SIZE.SMALL} />
                <p>Alla rätt!</p>
                <Divider size={DIVIDER_SIZE.SMALL} />

                <div className={'flex'}>
                  <Button
                    handleClick={() => reset()}
                    type={BUTTON_TYPE.SECONDARY}
                    margin
                  >
                    Gör om
                  </Button>
                  <Button
                    handleClick={onClosePanel}
                    type={BUTTON_TYPE.PRIMARY}
                    margin
                  >
                    Okej
                  </Button>
                </div>

                <Divider size={DIVIDER_SIZE.SMALL} />
              </div>
            ) : (
              <div style={{ flexBasis: '280px' }} className={'flex-none'}>
                <div
                  className={
                    'relative h-[160px] w-[160px] ml-auto mr-auto mt-8'
                  }
                >
                  {images.map((image) => (
                    <DraggableImage
                      image={image}
                      id={image.id}
                      key={image.id}
                      isDragging={draggingId === image.id}
                      fromDroppableId={IMAGE_LIST}
                    />
                  ))}
                </div>
                <div className={'text-center mt-4'}>
                  <Text size={TEXT_SIZES.SMALL}>
                    {allAnswersCorrect
                      ? ''
                      : `${images.length} / ${totalImages}`}
                  </Text>
                </div>
              </div>
            )}

            <div
              className={`bg-mediumGrey h-full grid grid-cols-${categories?.length} mb-[52px]`}
              style={{
                gridTemplateRows: 'auto 1fr',
                padding: '1px',
                gridGap: '1px',
              }}
            >
              {categories.map((category, index) => (
                <div key={index} className={'bg-white text-center w-full'}>
                  {category.name}
                </div>
              ))}
              {categories.map((category, index) => (
                <DroppableCategory
                  categoryIndex={index}
                  category={category}
                  key={index}
                  id={DROPPABLE_ID_PREFIX + index}
                  categoryCount={categories.length}
                  totalImages={totalImages}
                  onIncorrect={(image) => resetImage(image.id, index)}
                >
                  {category.placedImages.map((image, imageIndex) => (
                    <DraggableImage
                      image={image}
                      id={image.id}
                      key={image.id}
                      fromDroppableId={DROPPABLE_ID_PREFIX + index}
                      childInCategory={imageIndex}
                      isDragging={draggingId === image.id}
                    />
                  ))}
                </DroppableCategory>
              ))}
            </div>
          </div>
        </DndContext>
      </ExerciseHeader>
    </div>
  )
}

DragImageToCategory.propTypes = {
  name: PropTypes.string,
  wordPairs: PropTypes.array,
}

export default DragImageToCategory
